"use client";
import React from "react";
import HeroPage from "./components/HeroPage";
import { CardParent } from "./Contact1";
import { PropertyCardSkeleton } from "../design2/components/Skeletons/Design2PropertyCard";
import { useBranches } from "src/app/lib/fetchBranches";
import ReviewParent from "./components/Review";
import { headingTags, PropertyType } from "src/types";
import mockBranchData, { Branch } from "../../constants/branchData";
import { BranchData } from "./Branches";
import BranchCardSkeleton from "../design2/components/Skeletons/BranchSkeleton";
import { BranchSummaryCard } from "./Branches";
import CardGrid from "../../CardGrid";
import { ReviewType } from "../../constants/mockPosts";
import { Card } from "./components/Listing";
import { mockProperties } from "../../constants/mockProperties";
import Button from "../../ui/Button";
import Link from "next/link";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";
import { useState } from "react";
import { CompanyLogo } from "./NavBar";
import Skeleton from "react-loading-skeleton";
import { findPropertiesByTypeOptional } from "../../constants/mockProperties";
import Image from "next/image";
import { useFetchReviews } from "src/app/lib/useFetchReview";
import { generateSlugWithId } from "src/app/serverUtils";
const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
export function flattenRichText(html?: string) {
  if (!html) {
    return "No information provided";
  }
  return html.replace(/<[^>]*>/g, "").trim(); // Remove all HTML tags
}
export type Company = {
  name: string;
  logo: string;
  about: string;
  reviews: ReviewType[];
  recentListings: PropertyType[];
  branches: Branch[];
  companySummary: BranchData;
};
const About1 = ({ listings }: { listings: PropertyType[] }) => {
  const { data: branches, isLoading } = useBranches();
  const { data: item, isLoading: loading } = useFetchCompanyInfo();
  const summary = item?.companyStat;
  const isError = false;
  const propertyLoading = false;
  const properties = findPropertiesByTypeOptional();
  const dataToMap = isDemo ? properties : listings;
  const { data: reviews } = useFetchReviews();
  return (
    <section className="bg-red-100/5 max-w-full overflow-clip">
      <HeroPage
        subHeader="Real stories, real experiences. Explore our values and commitment to a seamless real estate journey."
        header="About Us & Reviews"
      />
      <div className="wrapper py-5">
        <div className="logo mb-8 flex md:flex-col  gap-3 items-center mx-auto w-max name">
          {loading ? (
            <div className="flex md:flex-col md:gap-2  gap-10 items-center">
              <Skeleton
                containerClassName="inline-block"
                height={50}
                width={250}
              />
              <Skeleton
                containerClassName="inline-block"
                height={40}
                width={200}
              />
            </div>
          ) : (
            <>
              <div className="image mx-auto  w-[clamp(300px,20vw,400px)] overflow-hidden rounded-md">
                <CompanyLogo height="80" width="100%" />
              </div>
              <h3 className="font-medium">{item?.companyInfo?.name}</h3>
            </>
          )}
        </div>
        <About
          loading={loading}
          maxLength={400}
          remove
          desc={item?.companyInfo?.about as string}
        />
        <div className="grid-container">
          <div className="branches-reviiew flex flex-col gap-3">
            <h2 className="text-primary font-medium">Branches</h2>
            {!isLoading && !branches?.length ? (
              <p>
                We’re currently updating our branch details. Soon, you’ll be
                able to view the locations, contact information, and services
                offered by our various branches across the region.Please check
                back shortly for the latest updates.
              </p>
            ) : (
              <CardGrid gap={40} maxColumns={1}>
                {isLoading ? (
                  <div className="flex flex-col gap-4">
                    <BranchCardSkeleton count={8} />
                  </div>
                ) : (
                  branches?.map((item, index) => (
                    <div key={index} className="py-4">
                      <BranchCard key={index} {...item} />
                    </div>
                  )) || <p>No branches available</p>
                )}
              </CardGrid>
            )}

            <ReviewParent reviews={reviews as ReviewType[]} />
          </div>
          <div
            id="child"
            className="second max-h-[800px] xlg:!max-h-[1000000px] child flex flex-col gap-10"
          >
            <BranchSummaryCard
              loading={loading}
              {...{ title: "Company Summary", values: { ...summary } }}
            />
            <div className="cards bg-white p-4 rounded-xl">
              <div className="first flex items-center mb-4 justify-between">
                <h2 className="text-primary h3 font-semibold">
                  Recent Listings
                </h2>
                {dataToMap.length > 0 && (
                  <Link
                    className="text-themeColor underline underline-offset-4"
                    href="/properties"
                  >
                    See More
                  </Link>
                )}
              </div>
              <CardGrid maxColumns={2}>
                {(() => {
                  if (isDemo && dataToMap?.length) {
                    return dataToMap.map((item, index) => (
                      <Card key={index} {...item} />
                    ));
                  }
                  if (propertyLoading) {
                    return Array.from({ length: 8 }).map((_, index) => (
                      <PropertyCardSkeleton key={index} />
                    ));
                  }
                  if (isError) {
                    return (
                      <div className="text-red-500">
                        There was an error loading properties.
                      </div>
                    );
                  }
                  if (dataToMap?.length) {
                    return dataToMap.map((item, index) => (
                      <Card key={index} {...item} />
                    ));
                  }
                  return (
                    <div className="text-gray-500">
                      Looks like there haven’t been any new property updates
                      yet.
                    </div>
                  );
                })()}
              </CardGrid>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default About1;

export const About: React.FC<{
  data?: string;
  title?: string;
  second?: boolean;
  loading?: boolean;
  maxLength?: number;
  desc?: string;
  remove?: boolean;
  className?: string;
  as?: headingTags;
}> = ({
  title,
  second,
  desc,
  remove,
  className,
  loading,
  maxLength,
  as = "h4",
}) => {
  const Head = as;
  if (!loading && !desc) {
    return null;
  }
  return (
    <div className={`parent mb-6 ${className}`}>
      {!loading}
      <CardParent className={` p-6 ${!remove && "border-themeColor border"}`}>
        <Head
          className={`title block font-semibold mb-2 pb-2 border-b  border-b-disabled-main/50 ${
            second && "!border-b-themeColor"
          } text-secondary-main`}
        >
          {loading ? (
            <Skeleton className="max-w-[400px]" />
          ) : (
            title || "About Us"
          )}
        </Head>
        <ShowMoreText
          loading={loading}
          maxLength={maxLength || 900}
          text={desc || "No information available"}
        />
      </CardParent>
    </div>
  );
};

const BranchCard: React.FC<Branch> = ({
  coverImage,
  id,
  branchName,
  address,
  aboutBranch,
}) => {
  const slugwithid = generateSlugWithId(Number(id), branchName);

  return (
    <div
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className="parent bg-white p-8 rounded-xl shadow-md"
    >
      <div className="flex gap-6">
        <Image
          height={400}
          width={400}
          src={coverImage}
          alt=""
          className="first size-[5.3rem] object-cover rounded-full"
        />
        <div className=" pb-6 mb-4 w-full border-b border-b-disabled2-main">
          <div className="name-addy mb-3">
            <h4 className="font-semibold text-secondary-main">{branchName}</h4>
            <h5 className="text-label-main h6 items-center flex gap-1">
              <AddressIcon />
              {address}
            </h5>
          </div>
          <div className="truncate-two-lines">
            {flattenRichText(aboutBranch)}
          </div>
        </div>
      </div>
      <Button
        href={`/branches/?branch=${slugwithid}`}
        className="block w-max primary-btn !text-[14px] px-8 text-base py-3 ml-auto"
      >
        See Details
      </Button>
    </div>
  );
};

export const companyMockData: Company = {
  name: "Pat Onukwuli & Co. Limited",
  logo: "/images/logo-image.webp",
  about: `
  <p>TechVision Ltd. is a globally recognized technology company dedicated to delivering cutting-edge solutions tailored to the evolving needs of modern businesses.</p>
  <p>With a relentless focus on innovation, we empower companies to thrive in the digital age through advanced software solutions, AI-driven insights, and seamless cloud integrations.</p>
  <p>Our team of industry experts is committed to ensuring that organizations, whether startups or Fortune 500 enterprises, achieve their technological goals efficiently and effectively.</p>
  <p>At TechVision Ltd., we take pride in our customer-centric approach, prioritizing user experience, data security, and scalability in all our solutions.</p>
  <p>Our services range from enterprise resource planning (ERP) systems and cybersecurity solutions to artificial intelligence implementation and blockchain technology.</p>
  <p>Since our inception, we have successfully helped thousands of businesses streamline operations, reduce costs, and enhance productivity through smart automation and cloud-based infrastructures.</p>
  <p>We believe in the power of digital transformation, and our mission is to make cutting-edge technology accessible to businesses of all sizes.</p>
  <p>With a global footprint, TechVision Ltd. continues to expand, fostering a culture of innovation, integrity, and excellence in the technology sector.</p>
`,
  reviews: [
    {
      id: "1",
      name: "Adebayo Gabriel",
      photo: "/images/person-1.webp",
      text: "Their professionalism, market knowledge, and attention to detail made the entire process seamless. They provided accurate property listings, guided us through every step, and ensured all paperwork was handled efficiently.",
      date: "2025-02-28",
      likes: 3,
      dislikes: 4,
      type: "positive",
      replies: [
        {
          id: "1-1",
          name: "John Onos",
          photo: "/images/person-2.webp",
          text: "I completely agree! Their team is amazing.",
          date: "2025-02-28",
          type: "positive",
          likes: 3,
          dislikes: 4,
          replies: [],
        },
      ],
    },
    {
      id: "1",
      name: "John Onos",
      photo: "/images/person-2.webp",
      text: "The service was okay, but communication and follow-through could have been better. The outcome was fine, but it lacked attention to detail.",
      date: "2025-02-28",
      type: "negative",
      likes: 3,
      dislikes: 4,
      replies: [
        {
          id: "1-1",
          name: "Abdulatif Mubarrack",
          photo: "/images/person-3.webp",
          text: "I completely agree! Their team is ass.",
          date: "2025-02-28",
          type: "positive",
          likes: 3,
          dislikes: 4,
          replies: [],
        },
      ],
    },
    {
      id: "1",
      name: "Abdulatif Mubarrack",
      photo: "/images/person-3.webp",
      text: "The team’s attention to detail was evident in how they vetted properties, ensuring I only viewed options that met my criteria. They were always available to answer my questions, explain complex processes, and offer valuable insights that made decision-making easier.",
      date: "2025-02-28",
      type: "negative",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
    {
      id: "1",
      name: "Tunde David ",
      photo: "/images/person-4.webp",
      text: "The team’s attention to detail was evident in how they vetted properties, ensuring I only viewed options that met my criteria. They were always available to answer my questions, explain complex processes, and offer valuable insights that made decision-making easier.",
      date: "2025-02-28",
      type: "positive",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
    {
      id: "1",
      name: "Wasiu Ismail",
      photo: "/images/person-5.webp",
      text: "The process had its positives and negatives. While helpful options were provided, the overall experience felt a bit disorganized, and communication could have been better. It was average overall.",
      date: "2025-02-28",
      type: "negative",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
    {
      id: "1",
      name: "Mubarak Binary",
      photo: "/images/person-6.webp",
      text: "The service was decent and showed good knowledge of the market, but the response time was slower than expected. The transaction was completed, but it could have been more efficient.",
      date: "2025-02-28",
      type: "neutral",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
    {
      id: "1",
      name: "Samson Samuel",
      photo: "/images/person-7.webp",
      text: "What stood out the most was their commitment to ensuring a smooth transaction. They handled all paperwork, coordinated with other parties, and kept me informed every step of the way. Even after the deal was completed, they followed up to ensure I was satisfied with my purchase and offered additional support.",
      date: "2025-02-28",
      type: "positive",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
    {
      id: "1",
      name: "Daniel Malik",
      photo: "/images/person-8.webp",
      text: "I had high expectations based on their reputation, but my experience was underwhelming. The agent was unprepared for property showings and seemed unfamiliar with the details. I expected a more professional approach, especially for a service this critical",
      date: "2025-02-28",
      type: "negative",
      likes: 3,
      dislikes: 4,
      replies: [],
    },
  ],
  recentListings: [...mockProperties.forRent],
  branches: [
    {
      id: 4,
      coverImage: "/images/article-10.webp",
      address: "23 Adeola Odeku Street, Victoria Island, Lagos",
      branchName: "Ikeja Branch",
      aboutBranch: `
      <p>The Ikeja branch serves as the commercial hub, supporting fintech and e-commerce innovation.</p>
      <p>Located in Lagos, it connects us to the business capital of Nigeria.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
    {
      id: 4,
      coverImage: "/images/article-9.webp",
      address: "Plot 112, Gwarinpa Expressway, Abuja",
      branchName: "Abuja Branch",
      aboutBranch: `
      <p>The Abuja branch focuses on government tech solutions and public-sector partnerships.</p>
      <p>Our strategic location in the capital ensures smooth collaboration with policymakers.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
    {
      id: 4,
      coverImage: "/images/article-8.webp",
      address: "Opp. University of Ibadan, Bodija, Ibadan",
      branchName: "Oyo State Branch",
      aboutBranch: `
      <p>The Oyo State branch is dedicated to research and development in AI and machine learning.</p>
      <p>Situated near Nigeria’s premier university, we drive innovation through academia.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
    {
      id: 4,
      coverImage: "/images/article-4.webp",
      address: "17 Trans-Amadi Industrial Layout, Port Harcourt",
      branchName: "Port Harcourt Branch",
      aboutBranch: `
      <p>The Port Harcourt branch supports the oil & gas sector with smart industrial technology.</p>
      <p>We drive digital transformation in Nigeria’s energy hub.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
    {
      id: 4,
      coverImage: "/images/article-5.webp",
      address: "14 Abdullahi Bayero Way, Kano",
      branchName: "Kano Branch",
      aboutBranch: `
      <p>The Kano branch connects us with Northern Nigeria’s booming trade and agritech industry.</p>
      <p>We support local businesses in adopting modern technology.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
    {
      id: 4,
      coverImage: "/images/article-6.webp",
      address: "5 Independence Layout, Enugu",
      branchName: "Enugu Branch",
      aboutBranch: `
      <p>The Enugu branch drives growth in media, entertainment, and creative industries.</p>
      <p>We collaborate with startups to power Nigeria’s creative economy.</p>
    `,
      branchSummary: mockBranchData.branchSummary,
    },
  ],

  companySummary: {
    title: "Company Summary",
    values: {
      "Year in Industries": "20 years +",
      "Total Branch": 12,
      "Total Staff": 13,
      "Properties for rent": 23,
      "Properties for sale": 45,
      "Properties for short let": 23,
      "Total Unit Managing": 45,
      "Total Reviews": 56,
      "Completed Transaction": 34,
    },
  },
};

export const AddressIcon = () => {
  return (
    <svg
      width="15"
      height="19"
      viewBox="0 0 15 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_631_47572)">
        <path
          d="M7.04314 10.634C8.33981 10.634 9.39096 9.58285 9.39096 8.28618C9.39096 6.98951 8.33981 5.93835 7.04314 5.93835C5.74647 5.93835 4.69531 6.98951 4.69531 8.28618C4.69531 9.58285 5.74647 10.634 7.04314 10.634Z"
          stroke="#606060"
          stroke-width="1.3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.04212 2.02429C5.38163 2.02429 3.78916 2.68392 2.61502 3.85806C1.44088 5.0322 0.78125 6.62467 0.78125 8.28516C0.78125 9.76586 1.09586 10.7347 1.95516 11.8069L7.04212 17.6765L12.1291 11.8069C12.9884 10.7347 13.303 9.76586 13.303 8.28516C13.303 6.62467 12.6434 5.0322 11.4692 3.85806C10.2951 2.68392 8.70261 2.02429 7.04212 2.02429Z"
          stroke="#606060"
          stroke-width="1.3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_631_47572">
          <rect
            width="14.087"
            height="18"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

// type ShowMoreTextProps = {
//   text: string;
//   maxLength?: number;
//   loading?: boolean;
//   as?: headingTags;
// };

export const ShowMoreText: React.FC<{
  text: string;
  maxLength?: number;
  loading?: boolean;
}> = ({ text, maxLength = 100, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  if (loading) return <Skeleton count={4} />;

  const shouldTruncate = text.length > maxLength;
  const displayText =
    isExpanded || !shouldTruncate ? text : text.slice(0, maxLength) + "...";

  return (
    <div className="text-secondary-main">
      <div
        className="prose prose-sm max-w-full break-words text-lg"
        dangerouslySetInnerHTML={{ __html: displayText }}
      />
      {shouldTruncate && (
        <button
          onClick={toggleExpansion}
          className="mt-2 text-themeColor hover:underline"
        >
          {isExpanded ? "Show Less" : "Show More"}
        </button>
      )}
    </div>
  );
};
